/**
 * useUIMode Hook
 * Manages public/internal UI mode with localStorage persistence.
 * Defaults to public on every fresh load for safety.
 */

import { useState, useCallback } from 'react';
import type { UIMode } from '../config/ui-modes';

const STORAGE_KEY = 'agentomy-ui-mode';

export function useUIMode(): {
  mode: UIMode;
  setMode: (mode: UIMode) => void;
  isInternal: boolean;
  activateInternal: (key: string) => boolean;
} {
  const [mode, setModeState] = useState<UIMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'internal' ? 'internal' : 'public';
    } catch {
      return 'public';
    }
  });

  const setMode = useCallback((newMode: UIMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, newMode);
    } catch {
      // localStorage may be unavailable; continue with in-memory state
    }
    setModeState(newMode);
  }, []);

  const activateInternal = useCallback((key: string): boolean => {
    // Simple key-based activation for now.
    // Replace with auth-based role check when user system exists.
    const validKey = (import.meta as any).env?.VITE_INTERNAL_KEY || 'agentomy-team-2026';
    if (key === validKey) {
      setMode('internal');
      return true;
    }
    return false;
  }, [setMode]);

  return { mode, setMode, isInternal: mode === 'internal', activateInternal };
}
